import {flattenPaths, getNestedValue} from "../../../utils/Utils";
import {
    Extension,
    mergeAttributes,
    Node,
    NodeViewWrapper,
    ReactNodeViewRenderer,
    ReactRenderer,
    useEditorState
} from "@tiptap/react";
import {Suggestion} from "@tiptap/suggestion";
import tippy from "tippy.js";
import classNames from "classnames";
import useVariables from "../../../contexts/variablesContext/useVariables";
import Button from "../../Button/Button";
import {SuggestionList} from "../suggestionList/SuggestionList";

export const VariableNode = Node.create({
    name: "variable",
    group: "inline",
    inline: true,
    atom: true,
    addAttributes() {
        return {
            path: {default: null},
        }
    },
    parseHTML() {
        return [{tag: "span[variable]"}]
    },
    renderHTML({node, HTMLAttributes}) {
        return ["span", mergeAttributes(HTMLAttributes, {"variable": node.attrs.path})]
    },
    addNodeView() {
        return ReactNodeViewRenderer(VariableComponent)
    },
})

const VariableComponent = ({node, editor, getPos, selected}) => {
    const {checkpointVariables} = useVariables();
    const value = getNestedValue(checkpointVariables, node.attrs.path)

    const handleSelectVariable = () => {
        const pos = getPos();
        editor.commands.setNodeSelection(pos);
    };

    return (
        <NodeViewWrapper as="span" contentEditable={false} style={{display: "inline"}}>
            <span onClick={handleSelectVariable}
                  style={{backgroundColor: selected ? "#8BB3F8" : "transparent", color: selected ? "#fff" : "inherit"}}>
                {value ?? `@${node.attrs.path}`}
            </span>
        </NodeViewWrapper>
    )
}

export const VariableListNode = Node.create({
    name: "variableList",
    inline: true,
    group: "inline",
    atom: true,
    addAttributes() {
        return {
            path: {default: null},
            format: {default: "inline"}
        }
    },
    parseHTML() {
        return [{tag: "span[variable-list]"}]
    },
    renderHTML({node, HTMLAttributes}) {
        return ["span", mergeAttributes(HTMLAttributes, {
            "variable-list": node.attrs.path,
            "format": node.attrs.format
        })]
    },
    addNodeView() {
        return ReactNodeViewRenderer(VariableListComponent)
    }
});

const toStringItems = (value) => {
    const arr = Array.isArray(value) ? value : [value]
    return arr.map(item =>
        typeof item === "object" && item !== null
            ? String(Object.values(item)[0])
            : String(item)
    )
}

const VariableListComponent = ({node, editor, getPos, selected}) => {
    const {checkpointVariables, diffVariables} = useVariables();
    const format = node.attrs.format ?? "inline";
    const newItems = toStringItems(getNestedValue(checkpointVariables, node.attrs.path));
    const oldItems = diffVariables ? toStringItems(getNestedValue(diffVariables, node.attrs.path)) : null;

    const handleSelect = () => editor.commands.setNodeSelection(getPos());

    const renderItems = () => {
        if (!oldItems) return newItems.map(item => ({oldItem: null, newItem: item, type: "normal"}));

        const maxLength = Math.max(oldItems.length, newItems.length);
        const result = [];

        for (let i = 0; i < maxLength; i++) {
            const oldItem = oldItems[i];
            const newItem = newItems[i];

            if (oldItem === undefined) {
                result.push({oldItem: null, newItem, type: "added"});
            } else if (newItem === undefined) {
                result.push({oldItem, newItem: null, type: "removed"});
            } else if (oldItem !== newItem) {
                result.push({oldItem, newItem, type: "modified"});
            } else {
                result.push({oldItem: null, newItem, type: "normal"});
            }
        }

        return result;
    }

    const items = renderItems();

    const getClass = (type) => {
        if (type === "added") return "diff-added";
        if (type === "removed") return "diff-removed";

        return "";
    }

    if (format === "inline") {
        return (
            <NodeViewWrapper as="span" contentEditable={false} style={{display: "inline"}}>
            <span onClick={handleSelect} style={{
                backgroundColor: selected ? "#8BB3F8" : "transparent",
                color: selected ? "#fff" : "inherit"
            }}>
                {items.map((entry, i) => (
                    <span key={i}>
                        {entry.type === "modified" ? (
                            <>
                                <span className="diff-removed">{entry.oldItem}</span>
                                {' '}
                                <span className="diff-added">{entry.newItem}</span>
                            </>
                        ) : (
                            <span className={getClass(entry.type)}>{entry.newItem ?? entry.oldItem}</span>
                        )}
                        {i < items.length - 1 ? ', ' : ''}
                    </span>
                ))}
            </span>
            </NodeViewWrapper>
        )
    }

    const Tag = format === "bullet" || format === "bulletList" ? "ul" : "ol";

    return (
        <NodeViewWrapper as="span" contentEditable={false} style={{display: "inline-block"}}>
            <Tag style={{
                margin: 0, paddingLeft: "20px",
                backgroundColor: selected ? "#8BB3F8" : "transparent",
                color: selected ? "#fff" : "inherit"
            }}>
                {items.map((entry, index) => (
                    <li key={index}>
                        {entry.type === "modified" ? (
                            <>
                                <span className="diff-removed">{entry.oldItem}</span>
                                {" "}
                                <span className="diff-added">{entry.newItem}</span>
                            </>
                        ) : (
                            <span className={getClass(entry.type)}>{entry.newItem ?? entry.oldItem}</span>
                        )}
                    </li>
                ))}
            </Tag>
        </NodeViewWrapper>
    )
}

export const VariableTableExtension = Node.create({
    name: "variableTable",
    group: "block",
    atom: true,
    addAttributes() {
        return {
            id: {
                default: null
            },
            sourcePath: {
                default: null
            },
            columns: {
                default: [],
                parseHTML: el => JSON.parse(el.getAttribute("columns") || "[]"),
                renderHTML: attrs => ({"columns": JSON.stringify(attrs.columns)})
            },
            oldColumns: {
                default: [],
                parseHTML: el => JSON.parse(el.getAttribute("oldColumns") || "[]"),
                renderHTML: attrs => ({"oldColumns": JSON.stringify(attrs.oldColumns)})
            },
            selected: {
                default: false,
                renderHTML: () => {
                }
            },
            class: {
                default: null,
                parseHTML: el => el.getAttribute("class"),
                renderHTML: attrs => attrs.class ? {class: attrs.class} : {}
            },
            // variableTable is atom node and marks only work on inline nodes,
            // so commentId and commentState must be stored as node attributes instead of using CommentMark
            commentId: {
                default: null,
                parseHTML: el => el.getAttribute("comment-id"),
                renderHTML: attrs => attrs.commentId ? {"comment-id": attrs.commentId} : {}
            },
            commentState: {
                default: null,
                parseHTML: el => el.getAttribute("comment-state"),
                renderHTML: attrs => attrs.commentState ? {
                    "comment-state": attrs.commentState,
                    class: `comment-${attrs.commentState}`
                } : {}
            }
        }
    },
    parseHTML() {
        return [{tag: 'div[data-type="variable-table"]'}];
    },
    renderHTML({HTMLAttributes}) {
        return ["div", mergeAttributes(HTMLAttributes, {"data-type": "variable-table"})];
    },
    addNodeView() {
        return ReactNodeViewRenderer((props) =>
            <VariableTableView {...props} onTable={this.options.onTable}/>
        );
    }
})

const VariableTableView = ({node, onTable, selected, editor}) => {
    const {sourcePath, columns, oldColumns, commentId, commentState} = node.attrs;
    const {checkpointVariables, diffVariables} = useVariables();
    const isEditable = useEditorState({
        editor,
        selector: (state) => state.editor.isEditable,
    });

    const tableClass = node.attrs.class;
    const isAdded = tableClass?.includes("diff-added");
    const isRemoved = tableClass?.includes("diff-removed");
    const isDiffMode = !!diffVariables && tableClass?.includes("diff-modified");

    const diffList = getNestedValue(diffVariables, sourcePath);
    const isNewSource = diffList === null || diffList === undefined;
    const newList = getNestedValue(isAdded && !isNewSource ? diffVariables : checkpointVariables, sourcePath) ?? [];
    const oldList = isDiffMode ? (getNestedValue(diffVariables, sourcePath) ?? []) : null;
    const oldColumnsMap = Object.fromEntries(
        (oldColumns ?? []).map(col => [col.key ?? col.name, col])
    );

    const resolvePath = (valuePath, rowIndex) => {
        const prefix = `${sourcePath}.`;
        return valuePath.startsWith(prefix)
            ? `${sourcePath}.${rowIndex}.${valuePath.slice(prefix.length)}`
            : valuePath;
    };

    const resolveValue = (valuePath, rowIndex, variables) => {
        if (!valuePath.includes("@")) return valuePath;

        return valuePath.replace(/@([\w.]+)/g, (_, path) => {
            const FORMATS = ["inline", "orderedList", "bulletList"];
            const parts = path.split(".");
            const formatIndex = parts.findIndex(p => FORMATS.includes(p));

            if (formatIndex !== -1) {
                const format = parts[formatIndex];
                const realPath = [...parts.slice(0, formatIndex), ...parts.slice(formatIndex + 1)].join(".");
                const value = getNestedValue(variables, resolvePath(realPath, rowIndex));

                if (!Array.isArray(value)) return value ?? "-";

                if (format === "inline") return value.join(", ");
                if (format === "orderedList") return value.map((v, i) => `${i + 1}. ${v}`).join("\n");
                if (format === "bulletList") return value.map(v => `• ${v}`).join("\n");
            }

            const value = getNestedValue(variables, resolvePath(path, rowIndex));
            return value ?? "-";
        });
    };

    const openTableModal = (e) => {
        e.preventDefault();
        e.stopPropagation();
        onTable?.(node);
    };

    const getCellContent = (col, rowIndex, isNewRow, isOldRow) => {
        if (isAdded || isRemoved) {
            const variables = isAdded && !isNewSource ? diffVariables : checkpointVariables;
            return {
                className: isAdded ? "diff-added" : "diff-removed",
                content: resolveValue(col.valuePath, rowIndex, variables),
            };
        }

        if (isNewRow) {
            const val = resolveValue(col.valuePath, rowIndex, diffVariables);
            return {className: "diff-removed", content: <span className="diff-removed">{val}</span>};
        }

        if (isOldRow) {
            return {
                className: "diff-added",
                content: resolveValue(col.valuePath, rowIndex, checkpointVariables),
            };
        }

        const oldCol = oldColumnsMap[col.key ?? col.name];

        const newVal = resolveValue(col.valuePath, rowIndex, checkpointVariables);
        const oldVal = isDiffMode
            ? resolveValue(oldCol?.valuePath ?? col.valuePath, rowIndex, diffVariables)
            : null;

        if (oldVal !== null && oldVal !== newVal) {
            return {
                className: "",
                content: (
                    <>
                        <span className="diff-removed">{oldVal}</span>
                        {" "}
                        <span className="diff-added">{newVal}</span>
                    </>
                ),
            };
        }

        return {className: "", content: newVal};
    };

    const maxRows = oldList ? Math.max(newList.length, oldList.length) : newList.length;
    const headerCellClass = isAdded ? "diff-added" : isRemoved ? "diff-removed" : "";

    return (
        <NodeViewWrapper>
            <div
                className={classNames("table-wrapper", commentState && `comment-${commentState}`, selected && "table-selected")}
                commentid={commentId}
            >
                {
                    isEditable &&
                    <Button
                        className="table-actions"
                        onMouseDown={openTableModal}
                    >
                        +
                    </Button>
                }

                <table className="variable-table" data-source={sourcePath}>
                    <tbody>
                    <tr style={{fontWeight: "bold"}}>
                        {columns.map((col, i) => (
                            <td key={i} className={headerCellClass}>{col.name}</td>
                        ))}
                    </tr>

                    {Array.from({length: maxRows}, (_, rowIndex) => {
                        const isNewRow = rowIndex >= newList.length;
                        const isOldRow = !!oldList && rowIndex >= oldList.length;

                        return (
                            <tr key={rowIndex}>
                                {columns.map((col, colIndex) => {
                                    const {className, content} = getCellContent(col, rowIndex, isNewRow, isOldRow);
                                    return <td key={colIndex} className={className}>{content}</td>;
                                })}
                            </tr>
                        );
                    })}

                    {newList.length === 0 && !oldList && !isAdded && (
                        <tr>
                            <td colSpan={columns.length}>No data in {sourcePath}</td>
                        </tr>
                    )}
                    </tbody>
                </table>
            </div>
        </NodeViewWrapper>
    );
};

export const VariableSuggestion = Extension.create({
    name: "variableSuggestion",
    addOptions() {
        return {
            getVariables: () => ({}),
        }
    },
    addProseMirrorPlugins() {
        return [
            Suggestion({
                editor: this.editor,
                char: "@",
                allowSpaces: false,
                items: ({query}) => {
                    const variables = this.options.getVariables() ?? {};
                    if (!variables || typeof variables !== "object") return [];

                    const COMMANDS = [
                        {type: "command", label: "table", value: "table"},
                        {type: "command", label: "inline", value: "inline"},
                        {type: "command", label: "orderedList", value: "orderedList"},
                        {type: "command", label: "bulletList", value: "bulletList"},
                    ];

                    if (!query) return COMMANDS;

                    const matchedCommand = COMMANDS.find(c => query.startsWith(c.value + "."));

                    if (matchedCommand) {
                        const search = query.slice(matchedCommand.value.length + 1);
                        const paths = flattenPaths(variables);

                        const filtered = paths.filter(path => {
                            const val = getNestedValue(variables, path);

                            if (!Array.isArray(val)) return false;
                            return !(val.length > 0 && typeof val[0] === "object" && val[0] !== null);
                        })

                        return filtered
                            .filter(path => path.toLowerCase().includes(search.toLowerCase()))
                            .map(path => ({
                                type: "variable",
                                label: `${matchedCommand.value}.${path}`,
                                value: path,
                                format: matchedCommand.value,
                            }))
                    }

                    const matchingCommands = COMMANDS.filter(c =>
                        c.label.toLowerCase().includes(query.toLowerCase())
                    )

                    const matchingScalars = flattenPaths(variables)
                        .filter(path => !Array.isArray(getNestedValue(variables, path)))
                        .filter(path => path.toLowerCase().includes(query.toLowerCase()))
                        .map(path => ({
                            type: "variable",
                            label: path,
                            value: path,
                            format: null,
                        }))

                    return [...matchingCommands, ...matchingScalars]
                },
                command: ({editor, range, props}) => {
                    if (props.type === "command") {
                        if (props.value === "table") {
                            editor.chain().focus().deleteRange(range).run();
                            this.options.onTable?.();
                            return;
                        }

                        editor.chain().focus().deleteRange(range).insertContent(`@${props.value}.`).run();
                        return;
                    }

                    editor.chain().focus().deleteRange(range).run();
                    const value = getNestedValue(this.options.getVariables(), props.value);

                    if (Array.isArray(value)) {
                        editor.commands.insertContent({
                            type: "variableList",
                            attrs: {path: props.value, format: props.format},
                        })
                    } else {
                        editor.commands.insertContent({
                            type: "variable",
                            attrs: {path: props.value},
                        })
                    }
                },
                render: () => {
                    let component;
                    let popup;
                    return {
                        onStart: (props) => {
                            component = new ReactRenderer(SuggestionList, {
                                props,
                                editor: props.editor,
                            });

                            popup = tippy("body", {
                                getReferenceClientRect: props.clientRect,
                                appendTo: () => document.body,
                                content: component.element,
                                showOnCreate: true,
                                interactive: true,
                                trigger: "manual",
                                placement: "bottom-start",
                            });
                        },
                        onUpdate: (props) => {
                            component?.updateProps(props);
                            popup?.[0]?.setProps({
                                getReferenceClientRect: props.clientRect,
                            });
                        },
                        onKeyDown: ({event}) => {
                            if (event.key === "Escape") {
                                popup?.[0]?.hide();
                                return true;
                            }

                            return (component?.ref)?.onKeyDown(event) ?? false;
                        },
                        onExit: () => {
                            popup?.[0]?.destroy();
                            component?.destroy();
                        },
                    }
                },
            }),
        ]
    },
})